/**
 * A sales invoice, at the speed of a counter.
 *
 * Customer first -- an account (on credit or paid now) or a walk-in cash
 * customer -- then the items: code, name or a scanned barcode (a carton's
 * barcode sells the carton), the price filled from the item or the pack,
 * and the stock in this store shown on every line before it is sold.
 *
 * What the server will refuse is said on the screen first: below the
 * minimum price without the right to go there, or more than the store
 * holds when the company does not allow it. Ctrl+Enter saves; the invoice
 * opens ready to print.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Autocomplete, Box, Button, IconButton, MenuItem, Paper, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { useTranslation } from 'react-i18next'

import { createSale, fetchCompany, fetchInvItems, fetchStores } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type InvItem } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useLedgers } from '../store/LedgerCache'
import { useLedgerBalances } from '../store/useLedgerBalances'
import { useUnsavedChanges } from '../store/UnsavedChanges'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import LedgerPicker from '../vouchers/LedgerPicker'
import Keycap from '../components/Keycap'
import ScreenTitle from '../components/ScreenTitle'
import { addMoney, sanitize } from '../vouchers/money'
import { toHijri } from '../reports/hijri'
import { today } from '../dates'
import { c, fmtMoney } from '../theme'
import { fmtQty, lineAmount } from './qty'
import { priceLine } from './PurchaseEntry'

interface Line {
  key: number
  item: InvItem | null
  unit: string
  qty: string
  price: string
  discount: string
}

let nextKey = 1
const blank = (): Line => ({ key: nextKey++, item: null, unit: '', qty: '', price: '', discount: '' })

export function factorOf(it: InvItem, unit: string): number {
  if (unit === it.base_unit) return 1
  return Number(it.units.find((u) => u.unit === unit)?.factor ?? 1)
}

/** The price a unit sells at: the pack's own, or so many base units' worth. */
export function listPrice(it: InvItem, unit: string): string {
  if (unit !== it.base_unit) {
    const pack = it.units.find((u) => u.unit === unit)
    if (pack?.sale_price) return Number(pack.sale_price).toFixed(2)
  }
  if (!it.sale_price) return ''
  return lineAmount(String(factorOf(it, unit)), it.sale_price)
}

/** The least this line may come to before it needs the override right. */
export function minimumFor(it: InvItem, unit: string, qty: string): string | null {
  if (!it.min_price || !qty) return null
  return lineAmount(qty, String(Number(it.min_price) * factorOf(it, unit)))
}

export default function SaleEntry() {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const nav = useNavigate()
  const qc = useQueryClient()
  const { can } = useAuth()
  const { ledgers } = useLedgers()
  const balances = useLedgerBalances()
  const mayOverride = can(P.priceOverride)

  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const items = useQuery({ queryKey: ['inv-items', '', '', 'active'],
                           queryFn: () => fetchInvItems({ active: true }) })
  const company = useQuery({ queryKey: ['company'], queryFn: fetchCompany })
  const allowNegative = !!company.data?.allow_negative_stock

  const [walkIn, setWalkIn] = useState(false)
  const [customer, setCustomer] = useState('')
  const [buyerName, setBuyerName] = useState('')
  const [buyerVat, setBuyerVat] = useState('')
  const [paidNow, setPaidNow] = useState(false)
  const [cashCode, setCashCode] = useState('')
  const [date, setDate] = useState(today())
  const [storeId, setStoreId] = useState<number | ''>('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<Line[]>([blank()])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (storeId === '' && stores.data?.length) {
      const first = stores.data.find((s) => s.is_active && s.kind === 'WAREHOUSE') ?? stores.data[0]
      setStoreId(first.id)
    }
  }, [stores.data, storeId])

  // A counter sale is paid into the till: offer the first cash account.
  const defaultCash = useMemo(() => ledgers.find((l) =>
    (l.group_keys ?? [l.group_key]).includes('CASH'))?.code ?? '', [ledgers])
  useEffect(() => {
    if ((walkIn || paidNow) && !cashCode && defaultCash) setCashCode(defaultCash)
  }, [walkIn, paidNow, cashCode, defaultCash])
  const paying = walkIn || paidNow

  const filled = lines.filter((l) => l.item)
  const priced = filled.map((l) => priceLine({ item: l.item, qty: l.qty, cost: l.price,
                                                discount: l.discount }))
  const subtotal = addMoney('0', ...priced.map((p) => p.net))
  const vat = addMoney('0', ...priced.map((p) => p.vat))
  const total = addMoney(subtotal, vat)

  // Per item: what this sale takes from the store, against what is there.
  const shortfalls = useMemo(() => {
    const need = new Map<number, { it: InvItem; q: number }>()
    for (const l of filled) {
      const q = (Number(l.qty) || 0) * factorOf(l.item!, l.unit)
      const prev = need.get(l.item!.id)
      need.set(l.item!.id, { it: l.item!, q: (prev?.q ?? 0) + q })
    }
    return [...need.values()].filter(({ it, q }) =>
      q > Number(it.stock[String(storeId)] ?? 0))
      .map(({ it, q }) => t('inv.shortOf', { code: it.code,
        have: fmtQty(it.stock[String(storeId)] ?? 0), need: fmtQty(q), unit: it.base_unit }))
  }, [filled, storeId, t])

  const belowMin = filled.filter((l) => {
    const m = minimumFor(l.item!, l.unit, l.qty)
    return m !== null && Number(priceLine({ item: l.item, qty: l.qty, cost: l.price,
                                            discount: l.discount }).net) < Number(m)
  })

  const dirty = !!customer || filled.length > 0 || !!buyerName
  const setLine = (key: number, patch: Partial<Line>) => setLines((ls) => {
    const next = ls.map((l) => (l.key === key ? { ...l, ...patch } : l))
    return next[next.length - 1].item ? [...next, blank()] : next
  })

  const cells = useRef<Record<string, HTMLInputElement | null>>({})
  const focusCell = (key: number, col: string) =>
    requestAnimationFrame(() => cells.current[`${key}:${col}`]?.focus())
  const ORDER = ['item', 'qty', 'price', 'discount']
  function onEnter(e: React.KeyboardEvent, l: Line, col: string) {
    if (e.key !== 'Enter' || e.ctrlKey || e.metaKey) return
    e.preventDefault()
    const i = ORDER.indexOf(col)
    // Most counter lines are item + quantity: Enter on the quantity goes
    // straight to the next item, the price having been filled already.
    if (col === 'qty' || i === ORDER.length - 1) {
      const idx = lines.findIndex((x) => x.key === l.key)
      const nextLine = lines[idx + 1]
      if (nextLine) focusCell(nextLine.key, 'item')
      return
    }
    focusCell(l.key, ORDER[i + 1])
  }

  function pickItem(key: number, it: InvItem | null, unit?: string) {
    if (!it) { setLine(key, { item: null, unit: '', qty: '', price: '', discount: '' }); return }
    const u = unit ?? it.base_unit
    setLine(key, { item: it, unit: u, qty: '1', price: listPrice(it, u) })
    focusCell(key, 'qty')
  }

  function changeUnit(l: Line, u: string) {
    if (!l.item) return
    setLine(l.key, { unit: u, price: listPrice(l.item, u) })
  }

  const save = useMutation({
    mutationFn: () => createSale({
      customer_code: walkIn ? null : customer,
      customer_name: walkIn ? (buyerName.trim() || null) : null,
      customer_vat: walkIn ? (buyerVat.trim() || null) : null,
      doc_date: date, store_id: Number(storeId),
      cash_ledger_code: paying ? cashCode : null, notes: notes.trim() || null,
      lines: filled.map((l) => ({ item_code: l.item!.code, qty: l.qty, unit: l.unit,
                                  price: l.price, discount: l.discount || null })),
    }),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ['inv-items'] })
      void qc.invalidateQueries({ queryKey: ['inv-sales'] })
      void qc.invalidateQueries({ queryKey: ['ledger-balances'] })
      setLines([blank()]); setCustomer(''); setBuyerName('')
      nav(`/inventory/sales/${s.id}`, { replace: true,
        state: { saved: true, warnings: s.stock_warnings ?? [] } })
    },
    onError: (e) => setError(errorMessage(e)),
  })
  useUnsavedChanges(dirty && !save.isPending && !save.isSuccess)

  const problems = [
    !walkIn && !customer && t('inv.needCustomer'),
    filled.length === 0 && t('inv.needLine'),
    filled.some((l) => !l.qty || Number(l.qty) <= 0) && t('inv.needQty'),
    filled.some((l) => l.price === '') && t('inv.needPrice'),
    paying && !cashCode && t('inv.needCash'),
    storeId === '' && t('inv.needStore'),
    belowMin.length > 0 && !mayOverride && t('inv.belowMinBlocked'),
    shortfalls.length > 0 && !allowNegative && t('inv.notEnoughStock'),
  ].filter(Boolean) as string[]
  const ready = problems.length === 0

  useHotkeys('sale-entry', [
    { combo: 'ctrl+enter', label: t('common.save'), allowInInput: true,
      handler: () => { if (ready && !save.isPending) save.mutate() } },
    { combo: 'alt+a', label: t('inv.addLine'), allowInInput: true,
      handler: () => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') } },
  ])

  const options = items.data?.items ?? []
  const typed = useRef<Record<number, string>>({})

  if (!can(P.postSales)) return <Alert severity="warning">{t('report.noPermission')}</Alert>

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <ScreenTitle titleKey="inv.newSale" sub={t('inv.newSaleSub')} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ px: 2.5, pt: 1, pb: 2 }}
          alignItems={{ md: 'flex-start' }}>
          <Box sx={{ flex: 2, minWidth: 0 }}>
            <ToggleButtonGroup exclusive size="small" value={walkIn ? 'walkin' : 'account'}
              sx={{ mb: 1.25 }} onChange={(_, v) => v && setWalkIn(v === 'walkin')}>
              <ToggleButton value="account">{t('inv.customerAccount')}</ToggleButton>
              <ToggleButton value="walkin">{t('inv.cashCustomer')}</ToggleButton>
            </ToggleButtonGroup>
            {walkIn ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField size="small" label={t('inv.buyerName')} value={buyerName} sx={{ flex: 1 }}
                  onChange={(e) => setBuyerName(e.target.value)} inputProps={{ maxLength: 160 }} />
                <TextField size="small" label={t('inv.buyerVat')} value={buyerVat} sx={{ flex: 1 }}
                  onChange={(e) => setBuyerVat(e.target.value.trim())}
                  helperText={buyerVat ? t('inv.fullTaxInvoice') : t('inv.simplifiedInvoice')}
                  inputProps={{ maxLength: 20, inputMode: 'numeric' }} />
              </Stack>
            ) : (
              <LedgerPicker value={customer} onChange={(code) => setCustomer(code)}
                groups={['DEBTORS']} autoFocus placeholder={t('inv.customerPlaceholder')}
                balanceOf={balances.of} />
            )}
          </Box>
          <Box sx={{ width: { md: 190 } }}>
            <TextField type="date" size="small" label={t('entry.date')} value={date} fullWidth
              InputLabelProps={{ shrink: true }} onChange={(e) => setDate(e.target.value)} />
            <Typography sx={{ fontSize: 11.5, color: c.inkMuted, mt: 0.4, px: 0.25 }}>
              {date ? toHijri(date, i18n.language) : ''}</Typography>
          </Box>
          <TextField select size="small" label={t('inv.fromStore')} value={storeId}
            sx={{ width: { md: 220 } }} onChange={(e) => setStoreId(Number(e.target.value))}>
            {(stores.data ?? []).filter((s) => s.is_active).map((s) => (
              <MenuItem key={s.id} value={s.id}>{(ar && s.name_ar) || s.name_en}</MenuItem>))}
          </TextField>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
          sx={{ px: 2.5, pb: 2 }}>
          {!walkIn && (
            <ToggleButtonGroup exclusive size="small" value={paidNow ? 'now' : 'credit'}
              onChange={(_, v) => v && setPaidNow(v === 'now')}>
              <ToggleButton value="credit">{t('inv.onCredit')}</ToggleButton>
              <ToggleButton value="now">{t('inv.paidNow')}</ToggleButton>
            </ToggleButtonGroup>
          )}
          {paying && (
            <Box sx={{ flex: 1, maxWidth: 360 }}>
              <LedgerPicker value={cashCode} onChange={(code) => setCashCode(code)}
                groups={['CASH', 'BANK']} placeholder={t('inv.receivedInto')} />
            </Box>
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ overflowX: 'auto', borderTop: `1px solid ${c.border}` }}>
          <table className="rpt" style={{ minWidth: 960 }}>
            <thead><tr>
              <th style={{ width: 32 }}>#</th>
              <th>{t('inv.item')}</th>
              <th style={{ width: 104 }}>{t('inv.unit')}</th>
              <th className="num" style={{ width: 90 }}>{t('inv.qty')}</th>
              <th className="num" style={{ width: 110 }}>{t('inv.price')}</th>
              <th className="num" style={{ width: 96 }}>{t('inv.discount')}</th>
              <th className="num" style={{ width: 110 }}>{t('inv.net')}</th>
              <th className="num" style={{ width: 90 }}>{t('inv.vat')}</th>
              <th style={{ width: 40 }} />
            </tr></thead>
            <tbody>
              {lines.map((l, idx) => {
                const p = priceLine({ item: l.item, qty: l.qty, cost: l.price, discount: l.discount })
                const units = l.item ? [l.item.base_unit, ...l.item.units.map((u) => u.unit)] : []
                const inStore = l.item ? l.item.stock[String(storeId)] ?? '0' : null
                const min = l.item ? minimumFor(l.item, l.unit, l.qty) : null
                const under = min !== null && Number(p.net) < Number(min)
                return (
                  <tr key={l.key}>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <Autocomplete<InvItem> size="small" options={options} value={l.item}
                        autoHighlight openOnFocus={false}
                        getOptionLabel={(o) => `${o.code} — ${(ar && o.name_ar) || o.name_en}`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onInputChange={(_, v) => { typed.current[l.key] = v }}
                        filterOptions={(opts, s) => {
                          const n = s.inputValue.trim().toLowerCase()
                          if (!n) return opts.slice(0, 50)
                          return opts.filter((o) => o.code.toLowerCase().includes(n)
                            || o.name_en.toLowerCase().includes(n) || (o.name_ar ?? '').includes(s.inputValue.trim())
                            || o.barcode === n || o.units.some((u) => u.barcode === n)).slice(0, 50)
                        }}
                        onChange={(_, v) => {
                          const scanned = (typed.current[l.key] ?? '').trim()
                          pickItem(l.key, v, v?.units.find((u) => u.barcode && u.barcode === scanned)?.unit)
                        }}
                        renderOption={(props, o) => (
                          <li {...props} key={o.id}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontSize: 14 }} noWrap>
                                <span className="doc-no">{o.code}</span> · {(ar && o.name_ar) || o.name_en}</Typography>
                              <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                                {t('inv.inThisStore', { qty: fmtQty(o.stock[String(storeId)] ?? 0), unit: o.base_unit })}
                                {o.sale_price ? ` · ${fmtMoney(o.sale_price)}` : ''}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(pr) => <TextField {...pr} placeholder={t('inv.pickItem')}
                          inputRef={(el) => { cells.current[`${l.key}:item`] = el }}
                          inputProps={{ ...pr.inputProps, 'aria-label': t('inv.itemOnLine', { n: idx + 1 }) }} />} />
                      {l.item && (
                        <Typography sx={{ fontSize: 11.5, px: 0.5, pt: 0.25,
                          color: under ? (mayOverride ? c.warningInk : c.dangerInk)
                            : Number(inStore) <= 0 ? c.dangerInk : c.inkMuted }}>
                          {t('inv.inThisStore', { qty: fmtQty(inStore), unit: l.item.base_unit })}
                          {under && ` · ${t(mayOverride ? 'inv.belowMinOk' : 'inv.belowMin',
                                            { min: fmtMoney(min!) })}`}
                        </Typography>
                      )}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <TextField select size="small" fullWidth value={l.item ? l.unit : ''}
                        disabled={!l.item} onChange={(e) => changeUnit(l, e.target.value)}
                        inputProps={{ 'aria-label': t('inv.unitOnLine', { n: idx + 1 }) }}>
                        {units.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                    </td>
                    {(['qty', 'price', 'discount'] as const).map((col) => (
                      <td key={col} style={{ padding: '4px 8px' }}>
                        <TextField size="small" value={l[col]} disabled={!l.item}
                          error={col === 'price' && under && !mayOverride}
                          inputRef={(el) => { cells.current[`${l.key}:${col}`] = el }}
                          onKeyDown={(e) => onEnter(e, l, col)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => setLine(l.key, { [col]: sanitize(e.target.value) })}
                          inputProps={{ inputMode: 'decimal', className: 'num', style: { textAlign: 'end' },
                            'aria-label': t(`inv.${col === 'price' ? 'priceOnLine' : `${col}OnLine`}`, { n: idx + 1 }) }} />
                      </td>
                    ))}
                    <td className="num">{l.item ? fmtMoney(p.net) : ''}</td>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{l.item ? fmtMoney(p.vat) : ''}</td>
                    <td>
                      {l.item && (
                        <IconButton size="small" aria-label={t('inv.removeLine')}
                          onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                          <DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Box>

        {shortfalls.length > 0 && (
          <Alert severity={allowNegative ? 'warning' : 'error'} sx={{ mx: 2.5, mt: 2 }}>
            {allowNegative ? t('inv.stockWillGoNegative') : t('inv.notEnoughStock')}{' '}
            {shortfalls.join(' · ')}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ px: 2.5, py: 2 }}
          alignItems={{ sm: 'flex-start' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') }}>
              {t('inv.addLine')}</Button>
            <Keycap combo="alt+a" />
          </Stack>
          <TextField size="small" label={t('inv.notes')} value={notes} sx={{ flex: 1 }}
            onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 300 }} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
        <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ flex: '1 1 360px' }}>
          {[[t('inv.lines'), String(filled.length)], [t('inv.subtotal'), fmtMoney(subtotal)],
            [t('inv.vat'), fmtMoney(vat)]].map(([k, v]) => (
            <Box key={k}>
              <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{k}</Typography>
              <Typography className="num" sx={{ fontSize: 16, fontWeight: 500, textAlign: 'start' }}>{v}</Typography>
            </Box>
          ))}
          <Box>
            <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{t('inv.total')}</Typography>
            <Typography className="num" sx={{ fontSize: 24, fontWeight: 600, textAlign: 'start' }}>
              SAR {fmtMoney(total)}</Typography>
          </Box>
        </Stack>
        <Box sx={{ flex: '1 1 300px', maxWidth: 420 }}>
          <Button fullWidth variant="contained" size="large" disabled={!ready || save.isPending}
            onClick={() => save.mutate()}>
            {t('inv.saveSale')}
            <Box component="span" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
              <Keycap combo="ctrl+enter" /></Box>
          </Button>
          <Typography sx={{ fontSize: 12, mt: 1,
            color: problems.length && filled.length ? c.warningInk : c.inkMuted }}>
            {problems.length && filled.length ? problems[0]
              : paying ? t('inv.salePostsCash') : t('inv.salePostsCredit')}</Typography>
        </Box>
      </Paper>
    </Stack>
  )
}
